import { openSnackbar } from 'api/snackbar';

// Simple error message configurations
const ERROR_MESSAGES = {
  0: 'Network error. Please check your connection.',
  400: 'Invalid request. Please check your input.',
  401: 'Please log in to continue.',
  403: 'You don\'t have permission for this action.',
  404: 'Resource not found.',
  409: 'This resource already exists.',
  422: 'Please check your input and try again.',
  429: 'Too many requests. Please wait a moment.',
  500: 'Server error. Please try again later.',
  502: 'Service temporarily unavailable.',
  503: 'Service under maintenance. Please try again later.'
};

// Simple error types
export interface SimpleApiError {
  status: number;
  message: string;
  details?: string;
  errors?: Record<string, string[]>;
}

/**
 * Parse API error from axios response
 */
export function parseApiError(error: any): SimpleApiError {
  if (error?.response) {
    // Server responded with error status
    const { status, data } = error.response;
    return {
      status,
      message: data || data?.message || data?.error || ERROR_MESSAGES[status as keyof typeof ERROR_MESSAGES] || 'An error occurred',
      details: data?.details,
      errors: data?.errors
    };
  } else if (error?.request) {
    // Request was made but no response received
    return {
      status: 0,
      message: ERROR_MESSAGES[0],
      details: 'No response received from server'
    };
  } else {
    // Something else happened
    return {
      status: 0,
      message: error?.message || 'An unexpected error occurred',
      details: error?.toString()
    };
  }
}

/**
 * Show error message in snackbar (prevents spam)
 */
const errorCache = new Map<string, number>();
const ERROR_COOLDOWN = 3000; // 3 seconds

export function showError(error: SimpleApiError | string | any, customMessage?: string): void {
  let message: string;
  
  if (typeof error === 'string') {
    message = error;
  } else if (error?.status) {
    message = customMessage || error.message;
    
    // Add validation errors if available
    if (error.errors && Object.keys(error.errors).length > 0) {
      const validationMessages = Object.entries(error.errors)
        .map(([field, messages]) => `${field}: ${(messages as string[]).join(', ')}`)
        .join('; ');
      message += ` ${validationMessages}`;
    }
  } else {
    message = error?.message || 'An unexpected error occurred';
  }

  // Check if we should show this error (avoid spam)
  const key = `${message}`;
  const now = Date.now();
  const lastShown = errorCache.get(key) || 0;
  
  if (now - lastShown < ERROR_COOLDOWN) {
    return; // Don't show duplicate errors
  }
  
  errorCache.set(key, now);

  // Show snackbar
  openSnackbar({
    action: false,
    open: true,
    message,
    variant: 'alert',
    alert: {
      color: 'error',
      variant: 'filled'
    },
    transition: 'Fade',
    anchorOrigin: {
      vertical: 'top',
      horizontal: 'right'
    },
    close: true,
    actionButton: false,
    dense: false,
    maxStack: 3,
    iconVariant: 'usedefault',
    hideIconVariant: false
  });
}

/**
 * Show success message
 */
export function showSuccess(message: string): void {
  openSnackbar({
    action: false,
    open: true,
    message,
    variant: 'alert',
    alert: {
      color: 'success',
      variant: 'filled'
    },
    transition: 'Fade',
    anchorOrigin: {
      vertical: 'top',
      horizontal: 'right'
    },
    close: true,
    actionButton: false,
    dense: false,
    maxStack: 3,
    iconVariant: 'usedefault',
    hideIconVariant: false
  });
}

/**
 * Show warning message
 */
export function showWarning(message: string): void {
  openSnackbar({
    action: false,
    open: true,
    message,
    variant: 'alert',
    alert: {
      color: 'warning',
      variant: 'filled'
    },
    transition: 'Fade',
    anchorOrigin: {
      vertical: 'top',
      horizontal: 'right'
    },
    close: true,
    actionButton: false,
    dense: false,
    maxStack: 3,
    iconVariant: 'usedefault',
    hideIconVariant: false
  });
}

/**
 * Show info message
 */
export function showInfo(message: string): void {
  openSnackbar({
    action: false,
    open: true,
    message,
    variant: 'alert',
    alert: {
      color: 'info',
      variant: 'filled'
    },
    transition: 'Fade',
    anchorOrigin: {
      vertical: 'top',
      horizontal: 'right'
    },
    close: true,
    actionButton: false,
    dense: false,
    maxStack: 3,
    iconVariant: 'usedefault',
    hideIconVariant: false
  });
}

/**
 * Handle API errors with automatic snackbar display
 */
export function handleApiError(error: any, customMessage?: string): SimpleApiError {
  const apiError = parseApiError(error);
  
  // Show error in snackbar
  showError(apiError, customMessage);
   
  // Handle specific error types
  if (apiError.status === 401) {
    // Clear user session if unauthorized
    localStorage.removeItem('currentUser');
    // Could redirect to login here if needed
  }
  
  return apiError;
}

/**
 * Simple retry function with exponential backoff
 */
export async function retryRequest<T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> {
  let lastError: any;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      
      if (attempt === maxRetries) {
        break;
      }
      
      // Exponential backoff
      const delay = baseDelay * Math.pow(2, attempt - 1);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
}

/**
 * Check if error is a specific status code
 */
export function isErrorStatus(error: any, status: number): boolean {
  if (error?.response?.status === status) return true;
  if (error?.status === status) return true;
  return false;
}

/**
 * Check if error is a network error
 */
export function isNetworkError(error: any): boolean {
  return !error?.response && error?.request;
}

/**
 * Check if error is a timeout error
 */
export function isTimeoutError(error: any): boolean {
  return error?.code === 'ECONNABORTED' || error?.message?.includes('timeout');
}

/**
 * Get user-friendly error message for form validation
 */
export function getValidationErrorMessage(field: string, errors: Record<string, string[]>): string {
  if (errors[field] && errors[field].length > 0) {
    return errors[field][0];
  }
  return '';
}

/**
 * Clear error cache
 */
export function clearErrorCache(): void {
  errorCache.clear();
}

/**
 * Check if an error is retryable
 */
export function isRetryableError(error: any): boolean {
  if (!error) return false;
  
  const status = error?.response?.status || error?.status;
  
  // Retryable status codes
  const retryableStatuses = [0, 408, 429, 500, 502, 503, 504];
  
  return retryableStatuses.includes(status);
}

/**
 * Get user-friendly error message
 */
export function getUserFriendlyMessage(error: any): string {
  if (typeof error === 'string') return error;
  
  const apiError = parseApiError(error);
  return apiError.message;
}
